import {type ConnectionOperation, DOCUMENT_FIELDS_PATH} from '@github-ui/optimistic-connection'
import {
  createConnectionMutationId,
  dispatchConnectionMutation,
} from '@github-ui/optimistic-connection/connectionMutationBridge'
import {commitMutation, graphql} from 'react-relay'
import type {Environment} from 'relay-runtime'

import {escapeOptimisticTitleHTML} from '../utils/helpers'
import type {
  UpdateIssueInput,
  updateIssueTitleMutation,
  updateIssueTitleMutation$data,
} from './__generated__/updateIssueTitleMutation.graphql'

function toIssueTitleOperation(data: updateIssueTitleMutation$data): ConnectionOperation | undefined {
  const issue = data.updateIssue?.issue
  if (!issue) return undefined
  return {
    type: 'setFields',
    id: issue.id,
    fields: {title: issue.title, titleHTML: issue.titleHTML},
  }
}

export function commitUpdateIssueTitleMutation({
  environment,
  input: {issueId, title},
  onError,
  onCompleted,
}: {
  environment: Environment
  input: {issueId: string; title: string | null}
  onError?: (error: Error) => void
  onCompleted?: (response: updateIssueTitleMutation$data) => void
}) {
  const inputHash: UpdateIssueInput = {
    id: issueId,
  }
  if (title !== null) {
    inputHash.title = title
  }

  const optimisticResponse: updateIssueTitleMutation$data = {
    updateIssue: {
      issue: {
        id: issueId,
        title: title || '',
        titleHTML: escapeOptimisticTitleHTML(title || ''),
      },
    },
  }

  const mutationId = createConnectionMutationId()
  const optimisticOperation = title !== null ? toIssueTitleOperation(optimisticResponse) : undefined

  const dispatchTerminalError = (error: Error) => {
    dispatchConnectionMutation({phase: 'error', mutationId, error})
  }

  if (optimisticOperation) {
    dispatchConnectionMutation({
      phase: 'optimistic',
      mutationId,
      ownerId: issueId,
      connectionPath: DOCUMENT_FIELDS_PATH,
      operation: optimisticOperation,
    })
  }

  let disposable
  try {
    disposable = commitMutation<updateIssueTitleMutation>(environment, {
      // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
      mutation: graphql`
        mutation updateIssueTitleMutation($input: UpdateIssueInput!) @raw_response_type {
          updateIssue(input: $input) {
            issue {
              id
              title
              titleHTML
            }
          }
        }
      `,
      variables: {
        input: inputHash,
      },
      onError: error => {
        dispatchTerminalError(error)
        onError?.(error)
      },
      onUnsubscribe: () => {
        dispatchTerminalError(new Error('Updating the issue title was cancelled'))
      },
      optimisticResponse,
      onCompleted: (response, errors) => {
        const operation = title !== null ? toIssueTitleOperation(response) : undefined
        // A missing issue payload is a failure regardless of whether the title was the field
        // being set, which is what the removed patch check was standing in for.
        if (!response.updateIssue?.issue || (title !== null && !operation)) {
          const error = new Error(errors?.[0]?.message ?? 'Failed to update issue title')
          dispatchTerminalError(error)
          onError?.(error)
          return
        }
        if (operation) {
          dispatchConnectionMutation({
            phase: 'completed',
            mutationId,
            ownerId: issueId,
            connectionPath: DOCUMENT_FIELDS_PATH,
            operation,
          })
        }
        onCompleted?.(response)
      },
    })
  } catch (error) {
    dispatchTerminalError(error instanceof Error ? error : new Error('Failed to update issue title'))
    throw error
  }

  return disposable
}
